// Single-call expander for recursive `@fun` statement execution.
// Example: expand one parsed call with local scope replacement and nested recursion.
import { resolveFunctionMathExpressions } from './math'
import { assignFunctionParams, createEmptyLocalScope, extractLocalDeclarations, replaceLocalReferences } from '../local'
import type { LuisFunctionCall, LuisFunctionDefinition } from '../types'
import type { ExpandNestedCalls, ExpansionContext } from './expand-types'
import { buildStatementCall, pushExpansionWarning } from './warnings'

// Build one function call expansion with local scope extraction and nested recursion.
/** @internal Build one function call expansion with local scope extraction and nested recursion. */
export function expandFunctionCall(
  definition: LuisFunctionDefinition,
  call: LuisFunctionCall,
  context: ExpansionContext,
  stack: string[],
  depth: number,
  lineNumber: number,
  expandNestedCalls: ExpandNestedCalls,
): string {
  // Check whether the current call would recurse into the active stack.
  if (stack.includes(definition.name)) {
    pushExpansionWarning(context, 'function-recursion', lineNumber, `Recursive call detected for function "${definition.name}".`)
    // Return fallback call text for recursive expansion cycles.
    return buildStatementCall(definition.name, call.args)
  } else if (call.args.length !== definition.params.length) {
    // Check whether the current call uses the expected argument count.
    if (context._collectWarnings) {
      throw new Error(buildFunctionArgumentMismatchError(definition.name, definition.params.length, call.args.length, lineNumber))
    }
    pushExpansionWarning(
      context,
      'function-argument-mismatch',
      lineNumber,
      `Function "${definition.name}" expects ${definition.params.length} arguments, got ${call.args.length}.`,
    )
    // Return empty output so invalid calls do not expand in non-debug mode.
    return ''
  } else {
    // Continue expansion with resolved local scope.
  }

  const localScope = createEmptyLocalScope()
  assignFunctionParams(localScope, definition.params, call.args)
  const extractedLocals = extractLocalDeclarations(definition.body, localScope)
  const withLocalValues = replaceLocalReferences(extractedLocals._source, extractedLocals._scope)
  const withResolvedMath = resolveFunctionMathExpressions(withLocalValues)
  // Return nested-expanded body output from the current function call.
  return expandNestedCalls(withResolvedMath, context, [...stack, definition.name], depth + 1, definition.line)
}

// Build one stable syntax error for function argument-count mismatches.
function buildFunctionArgumentMismatchError(name: string, expected: number, received: number, line: number): string {
  return `Invalid .luis syntax at line ${line}: function "${name}" expects ${expected} arguments, got ${received}.`
}
